import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode, FilterService } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { AssetTypeTemplate } from '../constants';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import useAssetTypesService from '../hooks/services/useAssetTypesService';
import { ruLocale } from '../lang/ruLocale';
import { AssetType } from '../types/interfaces';
import AssetTypeCard from '../ui/AssetTypeCard';
import { clearFilter } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

FilterService.register('custom_parameters', (value, filters) => {
  if (!filters || filters.trim() === '') {
    return true;
  }
  if (!value || !Array.isArray(value)) {
    return false;
  }
  const filterText = filters.toLowerCase().trim();
  return value.some(
    (taskCloseCode) =>
      taskCloseCode.label &&
      taskCloseCode.label.toLowerCase().includes(filterText)
  );
});

export default function AssetTypes() {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [assetType, setAssetType] = useState<AssetType[]>([]);
  const [filtredAssetType, setFiltredAssetType] = useState<AssetType[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [assetTypeToDelete, setAssetTypeToDelete] = useState<
    string | number | null
  >(null);
  const [selectedAssetsType, setSelectedAssetsType] =
    useState<AssetType>(AssetTypeTemplate);
  const [totalCount, setTotalCount] = useState(1);
  // Ensure the filters state is strictly typed to Record<string, DataTableFilterMetaData>
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    parameters: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });


  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const [loading, setLoading] = useState<boolean>(true);
  const parametersFilterRef = useRef<HTMLInputElement>(null);
  const assetTypeFilterRef = useRef<HTMLInputElement>(null);
  const globalFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const assetTypeCardRef = useRef<null | { handleCancel: () => void }>(null);

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  const isSelectedIdExist = isSelected(selectedAssetsType);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const assetTypesService = useAssetTypesService();
  const getAssetTypesCallback = useCallback(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const nameFilter = filters.name as DataTableFilterMetaData;
    const parametersFilter = filters.parameters as DataTableFilterMetaData;
    const globalFilter = filters.global as DataTableFilterMetaData;

    const serviceResult = assetTypesService.getAll({
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify([
        {
          field: 'name',
          value: nameFilter.value,
          operator: nameFilter.matchMode
        },
        {
          field: 'parameters',
          value: parametersFilter.value,
          operator: parametersFilter.matchMode
        },
        {
          field: 'global',
          value: globalFilter.value,
          operator: globalFilter.matchMode
        }
      ])
    });
    serviceResult.result.then((r) => {
      if (r) {
        setAssetType(r.itemsList);
        setFiltredAssetType(r.itemsList);
        setTotalCount((r.total === 0 ? 1 : r.total) ?? 1);
      }
      setLoading(false);
    });
    return () => serviceResult.cancel();
  }, [assetTypesService, filters, multiSortMeta]);

  useEffect(() => {
    const cancel = getAssetTypesCallback();
    return () => cancel();
  }, [getAssetTypesCallback]);

  useEffect(() => {
    setFiltredAssetType(assetType);
  }, [assetType]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = { ...filters }; // Create a shallow copy of filters
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedAssetsType(AssetTypeTemplate);
    setDialogVisible(true);
  };

  const parametersBodyTemplate = (assetTypeData: AssetType | undefined) => {
    if (
      !assetTypeData ||
      !assetTypeData.parameters ||
      !Array.isArray(assetTypeData.parameters)
    ) {
      console.error('Invalid parameters data:', assetTypeData);
      return null;
    }

    return assetTypeData.parameters.map((characteristic) => (
      <Tag
        style={{ background: 'rgb(225, 234, 245)', color: '#495057' }}
        className="mr-1"
        key={characteristic.id}
      >
        {characteristic.label}
      </Tag>
    ));
  };

  function filterArray(arr: AssetType[], searchText: string) {
    setFiltredAssetType(
      arr.filter((obj) => {
        for (const key in obj) {
          if (key === 'id') continue;
          const value = obj[key as keyof AssetType];
          if (value) {
            if (Array.isArray(value)) {
              for (const item of value) {
                if (typeof item === 'object' && item !== null) {
                  for (const subKey in item) {
                    if (subKey === 'id') continue;
                    if (
                      typeof item.label === 'string' &&
                      item.label
                        ?.toLowerCase()
                        .includes(searchText.toLowerCase())
                    ) {
                      return true;
                    }
                  }
                }
              }
            } else if (
              typeof value === 'string' &&
              value.toLowerCase().includes(searchText.toLowerCase())
            ) {
              return true;
            }
          }
        }
        return false;
      })
    );
  }

  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите тип объекта для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  }

  const deleteButtonHandle = () => {
    if (isSelectedIdExist) {
      setAssetTypeToDelete(selectedAssetsType.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите тип объекта для удаления',
        life: 3000
      });
    }
  }

  const handleDeleteConfirmation = () => {
    handleDelete(assetTypeToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (
    id: string | number,
    updatedAssetType: AssetType
  ) => {
    const result =
      await assetTypesService.addOrUpdate(updatedAssetType)?.result;
    getAssetTypesCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await assetTypesService.delete(parseInt(id.toString()))?.result;
      setSelectedAssetsType(AssetTypeTemplate);
      getAssetTypesCallback();
    }
  };

  const handleAdd = async (assetType: AssetType) => {
    const result = await assetTypesService.addOrUpdate(assetType)?.result;
    getAssetTypesCallback();
    return result;
  };

  const closeDialog = () => {
    setDialogVisible(false);
  };

  const onHideAssetTypeCard = () => {
    if (assetTypeCardRef.current !== null) {
      assetTypeCardRef.current.handleCancel();
    }
  };

  const header = (  
    <TablePageHeader
      tableName="assetTypes"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    if (isMobile) {
      return 'calc(100dvh - 170px)';
    }

    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="assetType-table wrapper wrapper--main">
      <Toast ref={toast} />
      <Dialog
        header="Тип объекта"
        visible={dialogVisible}
        style={isMobile 
          ? {width: '370px', minHeight: '520px' }
          : {width: '600px', minHeight: '520px' }
        }
        maximizable
        modal
        contentStyle={{ minHeight: '300px' }}
        onHide={() => onHideAssetTypeCard()}
        className="p-0"
      >
        <VisibilityWrapper code="assetType-card__roles">
          <AssetTypeCard
            assetsTypeProps={selectedAssetsType}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={assetTypeCardRef}
            isMobile={isMobile}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="тип объекта"
        entityName={selectedAssetsType?.name}
      />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedAssetsType!}
        onSelectionChange={(e) => setSelectedAssetsType(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedAssetsType(e.data as AssetType);
        }}
        metaKeySelection={false}
        scrollable
        sortMode={isMobile ? 'single' : 'multiple'}
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={filtredAssetType}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Типы объектов не найдены."
        onRowReorder={(e) => setAssetType(e.value)}
        onFilter={onFiltersChange}
        footer={
          <div className="p-paginator p-component p-paginator-bottom justify-content-end" style={{
            height: 60
          }}>
            <span>{`Всего: ${filtredAssetType.length > 0 ? totalCount : 0}`}</span>
          </div>
        }
        pt={{
          footer: {
            style: {
              padding: 'unset',
              border: 'unset'
            }
          }
        }}
      >
        <Column
          columnKey="name"
          sortable
          field="name"
          header="Тип объекта"
          filter
          onFilterClear={() =>
            clearFilter('name', filters, setFilters, assetTypeFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={assetTypeFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по типу объекта"
              />
            </div>
          }
          style={{ 
            width: '33%',
            minWidth: '250px'
          }}
        />
        <Column
          columnKey="parameters"
          field="parameters"
          header="Характеристики"
          body={parametersBodyTemplate}
          filter
          onFilterClear={() =>
            clearFilter('parameters', filters, setFilters, parametersFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={parametersFilterRef}
                onChange={handleInputChange('parameters')}
                placeholder="Поиск по характеристикам"
              />
            </div>
          }
          style={{ 
            width: '65%',
            minWidth: '300px'
          }}
        />
      </DataTable>
    </div>
  );
}
